import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";

const HtmlTooltip = ({
  html,
  children,
}: {
  html: string;
  children: React.ReactNode;
}) => {
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({
    top: 0,
    left: 0,
    width: 250,
  });

  const ref = useRef<HTMLSpanElement | null>(null);

  const updateTooltipPosition = () => {
    if (!ref.current) return;

    const rect = ref.current.getBoundingClientRect();
    const tooltipMaxWidth = 550;
    const padding = 16;

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    //* ---------- Determine width based on available space ----------
    const spaceLeft = rect.left;
    const spaceRight = viewportWidth - rect.right;

    const width =
      Math.min(
        tooltipMaxWidth,
        spaceRight > spaceLeft ? spaceRight - padding : spaceLeft - padding
      ) || 450;

    //* ---------- Horizontal positioning (ensuring no clipping) ----------
    let left = rect.left + rect.width / 2;

    if (left - width / 2 < padding) {
      left = width / 2 + padding;
    }

    if (left + width / 2 > viewportWidth - padding) {
      left = viewportWidth - width / 2 - padding;
    }

    //* ---------- Vertical positioning ----------
    let top = rect.top - 10; // default: above the element

    const estimatedHeight = 120; // safe assumption or measure using ref
    const wouldClipTop = top - estimatedHeight < padding;
    const wouldClipBottom =
      rect.bottom + estimatedHeight > viewportHeight - padding;

    // Flip below if top is clipped
    if (wouldClipTop && !wouldClipBottom) {
      top = rect.bottom + 10;
    }

    // If both above and below are clipped → place inside screen
    if (wouldClipTop && wouldClipBottom) {
      top = Math.min(rect.top, viewportHeight - estimatedHeight - padding);
    }

    setCoords({
      top: top + window.scrollY,
      left: left + window.scrollX,
      width,
    });
  };

  const showTooltip = () => {
    updateTooltipPosition();
    setVisible(true);
  };

  const hideTooltip = () => setVisible(false);

  // Recalculate on scroll/resize while visible
  useEffect(() => {
    if (!visible) return;

    window.addEventListener("scroll", updateTooltipPosition);
    window.addEventListener("resize", updateTooltipPosition);

    return () => {
      window.removeEventListener("scroll", updateTooltipPosition);
      window.removeEventListener("resize", updateTooltipPosition);
    };
  }, [visible]);

  const triggerTop = ref.current?.getBoundingClientRect().top ?? 0;

  return (
    <>
      <span
        ref={ref}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        className="inline-block"
      >
        {children}
      </span>

      {visible &&
        createPortal(
          <div
            className="fixed bg-black text-white py-4 px-2 rounded text-xs shadow-lg  z-[999999999] pointer-events-none"
            style={{
              top: coords.top,
              left: coords.left,
              maxWidth: coords.width,
              transform:
                coords.top < triggerTop
                  ? "translate(-50%, -100%)" // tooltip above
                  : "translate(-50%, 0%)", // tooltip below
              whiteSpace: "normal",
            }}
            dangerouslySetInnerHTML={{ __html: html }}
          />,
          document.body
        )}
    </>
  );
};

export default HtmlTooltip;
